import type { LatLngTuple } from 'leaflet';
import type { MoTravelDay, MoTravelTransportType } from '../types/moTravel.types';
import {
  findWaypointByKey,
  isStartWaypoint,
  type DayRoute,
  type LegRoadSpan,
  type RouteWaypoint,
} from './dayRoutes';
import { isSameStartPlace } from './startPlaces';
import { buildStopInsights, type StopInsight } from './stopInsights';
import { isKeyOnDay } from './stopKey';

export interface FocusedLeg {
  path: LatLngTuple[];
  roadSpan: LegRoadSpan;
  color: string;
  date: string;
  toIndex: number;
  transportType: MoTravelTransportType | null;
}

interface RouteFocus {
  waypointKeys: ReadonlySet<string>;
  dayDates: ReadonlySet<string>;
  leg: FocusedLeg | null;
}

export interface StopDetail {
  waypoint: RouteWaypoint;
  fromTitle: string | null;
  fromPosition: LatLngTuple | null;
  incomingPath: LatLngTuple[] | null;
  outgoingPath: LatLngTuple[] | null;
  dayPath: LatLngTuple[];
  insight: StopInsight | undefined;
  color: string;
  date: string;
}

export interface StartDetail {
  waypoint: RouteWaypoint;
  incomingPath: null;
  outgoingPath: LatLngTuple[] | null;
  dayPath: LatLngTuple[];
  color: string;
  day: MoTravelDay;
}

const FOCUS_WITHOUT_HIGHLIGHT: RouteFocus = {
  waypointKeys: new Set(),
  dayDates: new Set(),
  leg: null,
};

function startsNear(route: DayRoute, start: RouteWaypoint): boolean {
  const [firstWaypoint] = route.waypoints;
  if (!firstWaypoint || !isStartWaypoint(firstWaypoint)) return false;
  return isSameStartPlace(firstWaypoint.position, start.position);
}

function getStartFocus(routes: DayRoute[], start: RouteWaypoint): RouteFocus {
  const routesSharingStart = routes.filter((route) => startsNear(route, start));
  return {
    waypointKeys: new Set(
      routesSharingStart.flatMap((route) => route.waypoints.map((waypoint) => waypoint.key)),
    ),
    dayDates: new Set(routesSharingStart.map((route) => route.day.date)),
    leg: null,
  };
}

function getLegFocus(route: DayRoute, index: number, focusedWaypoint: RouteWaypoint): RouteFocus {
  const previousWaypoint = route.waypoints[index - 1];
  const incomingLegPath = route.legPaths[index - 1];
  const incomingRoadSpan = route.legRoadSpans[index - 1];
  if (!previousWaypoint || !incomingLegPath || !incomingRoadSpan) {
    return { waypointKeys: new Set([focusedWaypoint.key]), dayDates: new Set(), leg: null };
  }
  return {
    waypointKeys: new Set([previousWaypoint.key, focusedWaypoint.key]),
    dayDates: new Set(),
    leg: {
      path: incomingLegPath,
      roadSpan: incomingRoadSpan,
      color: route.color,
      date: route.day.date,
      toIndex: index,
      transportType: focusedWaypoint.stop?.transportType ?? null,
    },
  };
}

export function findRouteFocus(routes: DayRoute[], focusedKey: string | null): RouteFocus | null {
  if (!focusedKey) return null;

  const match = findWaypointByKey(routes, focusedKey);
  if (match) {
    return isStartWaypoint(match.waypoint)
      ? getStartFocus(routes, match.waypoint)
      : getLegFocus(match.route, match.index, match.waypoint);
  }

  const isOnShownDay = routes.some((route) => isKeyOnDay(focusedKey, route.day.date));
  return isOnShownDay ? FOCUS_WITHOUT_HIGHLIGHT : null;
}

export function findStartDetail(
  routes: DayRoute[],
  waypointKey: string | null,
): StartDetail | null {
  if (!waypointKey) return null;

  const match = findWaypointByKey(routes, waypointKey);
  if (!match || !isStartWaypoint(match.waypoint)) return null;

  const { route, index, waypoint } = match;
  return {
    waypoint,
    incomingPath: null,
    outgoingPath: route.legPaths[index] ?? null,
    dayPath: route.path,
    color: route.color,
    day: route.day,
  };
}

export function findStopDetail(routes: DayRoute[], waypointKey: string | null): StopDetail | null {
  if (!waypointKey) return null;

  const match = findWaypointByKey(routes, waypointKey);
  if (!match?.waypoint.stop) return null;

  const { route, index, waypoint } = match;
  const previousWaypoint = route.waypoints[index - 1];
  return {
    waypoint,
    fromTitle: previousWaypoint?.title ?? null,
    fromPosition: previousWaypoint?.position ?? null,
    incomingPath: route.legPaths[index - 1] ?? null,
    outgoingPath: route.legPaths[index] ?? null,
    dayPath: route.path,
    insight: buildStopInsights(route.day).get(waypoint.key),
    color: route.color,
    date: route.day.date,
  };
}
